import {
	addBreadcrumb,
	breadcrumbsIntegration,
	captureException,
	httpContextIntegration,
	inboundFiltersIntegration,
	init as initSentry,
	withScope as withSentryScope,
} from '@sentry/browser'

import { base32 } from './base32'
import { RequestError } from './errors'

declare const window: any

type InitOptions = {
	enabled?: boolean
	dataSourceName?: string
	release?: string
	environment?: string
	proxy?: any
}

let isSentryEnabled = false
let getData: () => Record<string, unknown> = () => ({})

const createRandomValue = () => base32.encode(crypto.getRandomValues(new Uint8Array(16)))

export const viewId = createRandomValue()

let correlationIdPrefix = ''
export const getCorrelationId = () => `${correlationIdPrefix}${viewId}-${createRandomValue()}`

export const setCorrelationIdPrefix = (prefix: string | undefined) => {
	correlationIdPrefix = prefix === undefined ? '' : `${prefix}-`
}

export const initErrorClient = (
	{ enabled = false, dataSourceName, release, environment }: InitOptions = {},
	getAdditionalData?: typeof getData,
) => {
	isSentryEnabled = enabled && Boolean(dataSourceName) && dataSourceName !== '-'
	if (getAdditionalData) {
		getData = () => {
			try {
				return getAdditionalData()
			} catch {
				return { failedAdditionalData: true }
			}
		}
	}

	if (isSentryEnabled) {
		initSentry({
			dsn: dataSourceName,
			environment,
			release,
			integrations: [
				inboundFiltersIntegration(),
				httpContextIntegration(),
				breadcrumbsIntegration({
					sentry: true,

					console: false,
					dom: false,
					fetch: false,
					history: false,
					xhr: false,
				}),
			],
			autoSessionTracking: false,
		})
	}

	window.addEventListener('unhandledrejection', (event: { reason?: any }) => {
		const error = event.reason

		if (error && (error instanceof Error || error.message)) {
			logError(error)
		} else {
			logError(new Error(`Unhandled rejection of non-error '${error}'`))
		}
	})

	window.onerror = (message: string, url?: string, line = 0, column = 0, error?: any) => {
		if (message === 'Script error.') {
			console.error('External script error')
			return
		}
		if (message === 'ResizeObserver loop completed with undelivered notifications.') {
			console.warn(error ?? message)
			return
		}

		if (error && (error instanceof Error || error.message)) {
			logError(error)
		} else {
			const syntheticError = new Error(message)
			syntheticError.stack = `${message}\n    at ${url}:${line}:${column}`
			logError(syntheticError)
		}
	}

	// Check if the script load error handler has fired, and log this for analytics
	if (window.scriptLoadError) {
		const { scriptLoadError } = window
		delete window.scriptLoadError
		console.log(`Recovered from script load error in ${scriptLoadError.src}`)

		// Do not log, no proper way to react to it, and it is very noisy

		// // Log async to ensure getAdditionalData is not called immediately
		// setTimeout(() => {
		// 	logError(new Error('Recovered from script load failure'), { scriptLoadError, level: 'info' })
		// }, 0)
	}
}

export const leaveBreadcrumb = (data: any) => {
	if (isSentryEnabled) addBreadcrumb(data)
}

const getErrorFingerprint = (error: any) => {
	if (error instanceof RequestError) {
		return [error.request.route, error.request.method, String(error.request.statusCode)]
	}

	return ['{{ default }}']
}

export const logError = (error: any, data?: any) => {
	const payload = {
		...data,
		...error?.data,
		...getData(),
	}

	if (error) delete error.data

	logger.error(error, payload)

	const level = error.level ?? payload.level ?? 'error'

	if (isSentryEnabled && !error?.isHandled && level === 'error') {
		const { level, user = null, tags = {}, ...extra } = payload
		withSentryScope((scope) => {
			scope.setUser(user)
			scope.setTags(tags)
			if (typeof extra.correlationId === 'string' && extra.correlationId) {
				scope.setTag('correlation.id', extra.correlationId)
			}
			scope.setLevel('error')
			// Send as context instead? Doesn't support complex objects.
			// Object.entries(extra).forEach(([key, value]) => {
			// 	scope.setContext(upperCase(key), value as any)
			// })
			scope.setExtras(extra)
			scope.setFingerprint(getErrorFingerprint(error))
			captureException(error)
		})
	}
}

const noop = () => {}

export const logger =
	process.env.NODE_ENV === 'production'
		? {
				error: (...args: Array<any>) => console.error(...args),
				warn: noop,
				log: noop,
				verbose: noop,
			}
		: {
				error: (...args: Array<any>) => console.error(...args),
				warn: (...args: Array<any>) => console.warn(...args),
				log: (...args: Array<any>) => console.log(...args),
				verbose: (...args: Array<any>) => console.info(...args),
			}

export const loggerBlock = {
	error: logger.error,
	server: logger.log,
}
